import { useCallback, useEffect, useMemo, useRef } from 'react'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'
import { createLogger } from '@/lib/logger'

import { ExtendedSocket, SocketEvent } from '@scrambl/shared'

import { BroadcastEventType, useRealtime } from './useRealtime'

const logger = createLogger('lib/realtime/core/useEntityRealtime')

// Debug mode flag
const isBrowser =
  typeof window !== 'undefined' && typeof localStorage !== 'undefined'
const DEBUG_MODE =
  import.meta.env.DEV &&
  isBrowser &&
  localStorage.getItem('REALTIME_DEBUG') === 'true'

// Helper functions for conditional logging
function debugLog(..._args: unknown[]) {
  // Debug logging disabled - enable REALTIME_DEBUG in localStorage
}

/**
 * Supported entity types
 */
export type EntityType =
  | 'thread'
  | 'task'
  | 'project'
  | 'user'
  | 'organization'
  | 'note'

/**
 * Common entity event types
 */
export interface EntityEventMap {
  created: string
  updated: string
  deleted: string
}

/**
 * Common options for all entity realtime hooks
 */
export interface EntityRealtimeOptions {
  // Entity-agnostic callbacks
  onEntityCreated?: (data: Record<string, unknown>) => void
  onEntityUpdated?: (data: Record<string, unknown>) => void
  onEntityDeleted?: (data: Record<string, unknown>) => void
  // Common options
  throttleEvents?: boolean
  suppressLogging?: boolean
  events?: Record<string, string> // Custom event mapping
}

// Ensure connection registry is truly global by attaching to window
declare global {
  interface Window {
    __entityConnections?: {
      thread: Map<string, number>
      task: Map<string, number>
      project: Map<string, number>
      user: Map<string, number>
      organization: Map<string, number>
      note: Map<string, number>
    }
  }
}

// Initialize or get existing connection registry from window
if (typeof window !== 'undefined' && !window.__entityConnections) {
  window.__entityConnections = {
    thread: new Map<string, number>(),
    task: new Map<string, number>(),
    project: new Map<string, number>(),
    user: new Map<string, number>(),
    organization: new Map<string, number>(),
    note: new Map<string, number>(),
  }
}

/**
 * Registry to track entity connections
 */
const connectedEntities =
  typeof window !== 'undefined' && window.__entityConnections
    ? window.__entityConnections
    : {
        thread: new Map<string, number>(),
        task: new Map<string, number>(),
        project: new Map<string, number>(),
        user: new Map<string, number>(),
        organization: new Map<string, number>(),
        note: new Map<string, number>(),
      }

/**
 * Default socket message names for each entity type
 */
const defaultSocketNames: Record<EntityType, EntityEventMap> = {
  thread: {
    created: SocketEvent.THREAD_CREATED,
    updated: SocketEvent.THREAD_UPDATED,
    deleted: SocketEvent.THREAD_DELETED,
  },
  task: {
    created: SocketEvent.TASK_CREATED,
    updated: SocketEvent.TASK_UPDATED,
    deleted: SocketEvent.TASK_DELETED,
  },
  project: {
    created: SocketEvent.PROJECT_CREATED,
    updated: SocketEvent.PROJECT_UPDATED,
    deleted: SocketEvent.PROJECT_DELETED,
  },
  user: {
    // User events - these may not exist yet in the socket events
    created: 'user:created',
    updated: 'user:updated',
    deleted: 'user:deleted',
  },
  organization: {
    // Organization events - typically we listen for thread events
    // in the organization context, not org events themselves
    created: 'organization:created',
    updated: 'organization:updated',
    deleted: 'organization:deleted',
  },
  note: {
    created: SocketEvent.NOTE_CREATED,
    updated: SocketEvent.NOTE_UPDATED,
    deleted: SocketEvent.NOTE_DELETED,
  },
}

export interface EntityEventListenerOptions {
  resolveEntityId?: (data: Record<string, unknown>) => string | undefined
  skipBroadcast?: boolean
}

/**
 * Map socket message names to broadcast message types
 */
const socketToBroadcastTypeMap: Record<string, BroadcastEventType> = {
  [SocketEvent.THREAD_CREATED]: BroadcastEventType.THREAD_CREATED,
  [SocketEvent.THREAD_UPDATED]: BroadcastEventType.THREAD_UPDATED,
  [SocketEvent.THREAD_DELETED]: BroadcastEventType.THREAD_DELETED,
  [SocketEvent.THREAD_COMMENT_CREATED]:
    BroadcastEventType.THREAD_COMMENT_CREATED,
  [SocketEvent.THREAD_COMMENT_UPDATED]:
    BroadcastEventType.THREAD_COMMENT_UPDATED,
  [SocketEvent.THREAD_COMMENT_DELETED]:
    BroadcastEventType.THREAD_COMMENT_DELETED,
  [SocketEvent.THREAD_REACTION_ADDED]: BroadcastEventType.THREAD_REACTION_ADDED,
  [SocketEvent.THREAD_REACTION_REMOVED]:
    BroadcastEventType.THREAD_REACTION_REMOVED,
  [SocketEvent.COMMENT_REACTION_ADDED]:
    BroadcastEventType.COMMENT_REACTION_ADDED,
  [SocketEvent.COMMENT_REACTION_REMOVED]:
    BroadcastEventType.COMMENT_REACTION_REMOVED,
  [SocketEvent.TASK_UPDATED]: BroadcastEventType.TASK_UPDATED,
  [SocketEvent.TASK_STATUS_UPDATED]: BroadcastEventType.TASK_STATUS_UPDATED,
}

/**
 * Base hook for all entity-specific realtime functionality
 * Provides common room management and message handling
 */
export function useEntityRealtime(
  entityType: EntityType,
  entityId: string | null | undefined,
  options: EntityRealtimeOptions = {},
) {
  // Core realtime functionality
  const {
    socket,
    isReady,
    socketId,
    currentOrgId,
    joinRoom,
    leaveRoom,
    markEventProcessed,
    hasProcessedEvent,
    addEventListener,
    processSocketEvent,
    broadcastToTabs,
  } = useRealtime({
    throttleEvents: options.throttleEvents,
    suppressLogging: options.suppressLogging,
  })

  // Keep track of client-generated entities to prevent duplicates - now purely in-memory
  const clientGeneratedEntitiesRef = useRef<Set<string>>(new Set<string>())

  const optionsRef = useRef(options)
  const instanceId = useRef(
    `entity-${Math.random().toString(36).substring(2, 8)}`,
  )

  // Get the appropriate connection registry
  const connectionRegistry = connectedEntities[entityType]

  // Update options ref when options change (no deps to avoid re-renders)
  useEffect(() => {
    optionsRef.current = options
  })

  // Get socket names for this entity type (memoized to prevent re-renders)
  const socketNames = useMemo(() => {
    const defaults = defaultSocketNames[entityType]
    return {
      ...defaults,
      ...(optionsRef.current.events || {}),
    }
  }, [entityType])

  // Invoke callback if provided
  const invokeCallback = useCallback(
    (
      callbackName: keyof EntityRealtimeOptions,
      data: Record<string, unknown>,
    ) => {
      const callback = optionsRef.current[callbackName]
      if (typeof callback === 'function') {
        try {
          callback(data)
        } catch (error) {
          logger.error(
            `[useEntityRealtime:${instanceId.current}] Error in ${callbackName} callback:`,
            error,
          )
        }
      }
    },
    [],
  )

  // Create a socket message handler that delegates to the appropriate callback
  const createSocketHandler = useCallback(
    (
      eventType: 'created' | 'updated' | 'deleted',
      callbackName: keyof EntityRealtimeOptions,
      socketEventName: string,
    ) => {
      return (data: Record<string, unknown>) => {
        if (!entityId) return

        // Skip if this event isn't for the current entity
        const idField = `${entityType}Id`
        if (data.id !== entityId && data[idField] !== entityId) {
          return
        }

        // Organization context validation - more thorough check
        if (currentOrgId) {
          // If the data includes an organizationId, verify it matches the current context
          if (data.organizationId && data.organizationId !== currentOrgId) {
            debugLog(
              `[useEntityRealtime:${instanceId.current}] Organization mismatch: expected ${currentOrgId}, got ${data.organizationId}`,
            )
            return
          }
        } else if (!optionsRef.current.suppressLogging) {
          // No organization context available
          debugLog(
            `[useEntityRealtime:${instanceId.current}] No organization context available for ${entityType} ${eventType} event`,
          )
        }

        // Create a unique message ID for deduplication
        const idToUse = data.id || data[idField]
        const eventId = `${entityType}-${eventType}-${idToUse}-${Date.now()}`

        // Skip if already processed
        if (hasProcessedEvent(eventId)) {
          debugLog(
            `[useEntityRealtime:${instanceId.current}] Skipping already processed event: ${eventId}`,
          )
          return
        }

        // Check if this is a client-generated entity
        if (
          eventType === 'created' &&
          clientGeneratedEntitiesRef.current.has(idToUse as string)
        ) {
          debugLog(
            `[useEntityRealtime:${instanceId.current}] Skipping client-generated ${entityType}: ${idToUse}`,
          )
          return
        }

        // Mark as processed to prevent duplicate handling
        markEventProcessed(eventId)

        debugLog(
          `[useEntityRealtime:${instanceId.current}] Processing ${entityType} ${eventType} message for ${idToUse}`,
        )

        // Invoke callback
        invokeCallback(callbackName, data)

        // Only broadcast if this wasn't already from a broadcast
        if (data._source !== 'broadcast') {
          // Process socket message and broadcast to other tabs
          // Get corresponding broadcast type or use socket name as fallback
          const broadcastEventType =
            socketToBroadcastTypeMap[socketEventName] || socketEventName
          debugLog(
            `[useEntityRealtime:${instanceId.current}] Preparing to process and broadcast. SocketName: ${socketEventName}, BroadcastType: ${broadcastEventType}, Entity: ${entityType}:${idToUse}, Data:`,
            data,
          )

          // Add source tracking to prevent infinite broadcast loops
          const dataWithSource = {
            ...data,
            _source: 'broadcast',
          }

          processSocketEvent(
            broadcastEventType,
            entityType,
            entityId,
            dataWithSource,
          )
        } else {
          debugLog(
            `[useEntityRealtime:${instanceId.current}] Skipping re-broadcast of event as it came from another tab`,
          )
        }
      }
    },
    [
      entityId,
      entityType,
      currentOrgId,
      hasProcessedEvent,
      markEventProcessed,
      processSocketEvent,
      invokeCallback,
    ],
  )

  // Track if this specific hook instance has already incremented the counter
  const hasIncrementedRef = useRef(false)
  const previousEntityIdRef = useRef<string | null>(null)
  // Track if THIS instance should be responsible for joining the room
  // This is determined at increment time, not at room-join time, to avoid race conditions
  const shouldJoinRoomRef = useRef(false)
  // Track socket ID to detect reconnections - when socket ID changes, we need to rejoin rooms
  const lastSocketIdRef = useRef<string | null>(null)

  // Increment connection counter for this entity (only on mount or entityId change)
  useEffect(() => {
    if (!entityId) return

    // Check if entityId changed
    const entityIdChanged = previousEntityIdRef.current !== entityId
    previousEntityIdRef.current = entityId

    // Only increment if this is a new entityId for this hook instance
    if (entityIdChanged && !hasIncrementedRef.current) {
      const count = connectionRegistry.get(entityId) || 0
      const newCount = count + 1
      connectionRegistry.set(entityId, newCount)
      hasIncrementedRef.current = true
      // If we're the first connection (count was 0), this instance should join the room
      shouldJoinRoomRef.current = count === 0

      debugLog(
        `[useEntityRealtime:${instanceId.current}] INCREMENT connection for ${entityType}:${entityId}: ${count} -> ${newCount}, shouldJoinRoom: ${shouldJoinRoomRef.current}`,
      )
    }

    // Cleanup only runs when component truly unmounts or entityId changes
    return () => {
      // Only decrement if we actually incremented
      if (hasIncrementedRef.current && entityIdChanged) {
        const currentCount = connectionRegistry.get(entityId) || 0
        if (currentCount <= 1) {
          connectionRegistry.delete(entityId)
          debugLog(
            `[useEntityRealtime:${instanceId.current}] REMOVE connection for ${entityType}:${entityId}: ${currentCount} -> 0`,
          )
        } else {
          const decrementedCount = currentCount - 1
          connectionRegistry.set(entityId, decrementedCount)
          debugLog(
            `[useEntityRealtime:${instanceId.current}] DECREMENT connection for ${entityType}:${entityId}: ${currentCount} -> ${decrementedCount}`,
          )
        }
        hasIncrementedRef.current = false
        shouldJoinRoomRef.current = false
      }
    }
  }, [entityId, entityType, connectionRegistry])

  // Set up socket listeners and register for cross-tab broadcasts
  useEffect(() => {
    if (!socket || !entityId) return

    const isClientPortalRoute = !!getCurrentClientPortalContext()?.entityId

    // If the organization ID isn't available yet, retry later
    if (!currentOrgId && !isClientPortalRoute) {
      const retryTimer = setTimeout(() => {
        if (!optionsRef.current.suppressLogging) {
          debugLog(
            `[useEntityRealtime:${instanceId.current}] Organization context not available for ${entityType} ${entityId}, will retry connection`,
          )
        }
      }, 2000)
      return () => clearTimeout(retryTimer)
    }

    // Detect socket reconnection - when socket ID changes, we need to rejoin the room
    const currentSocketId = socketId ?? socket.id
    const previousSocketId = lastSocketIdRef.current
    const socketChanged =
      previousSocketId !== null && previousSocketId !== currentSocketId

    // Update tracked socket ID
    lastSocketIdRef.current = currentSocketId ?? null

    // If socket changed (reconnection), force this instance to rejoin
    // This is critical - after reconnection, all rooms need to be rejoined
    if (socketChanged) {
      logger.debug(
        `[useEntityRealtime:${instanceId.current}] Socket reconnected (${previousSocketId} -> ${currentSocketId}), will rejoin ${entityType}:${entityId}`,
      )
      // Only the instance that owns the room join should trigger rejoin.
      // Other instances keep their existing ownership flag and skip duplicate joins.
      shouldJoinRoomRef.current =
        shouldJoinRoomRef.current ||
        (connectionRegistry.get(entityId) || 0) <= 1
    }

    // Use the flag set during increment time to determine if this instance should join
    // This avoids race conditions when multiple hooks increment before any join
    const isFirstConnection = shouldJoinRoomRef.current

    if (!isFirstConnection) {
      const currentCount = connectionRegistry.get(entityId) || 0
      debugLog(
        `[useEntityRealtime:${instanceId.current}] Skipping room join for ${entityType}:${entityId}, count=${currentCount}, shouldJoinRoom=${shouldJoinRoomRef.current}`,
      )
    }

    if (isFirstConnection) {
      // Join entity room (joinRoom will check for duplicates internally)
      logger.debug(
        `[useEntityRealtime] Joining ${entityType}:${entityId} room`,
        {
          instanceId: instanceId.current,
          socketId: currentSocketId,
          socketChanged,
        },
      )
      const joined = joinRoom(entityType, entityId)
      if (joined) {
        logger.debug(`[useEntityRealtime] Join ${entityType}:${entityId} queued`)
      } else {
        logger.debug(
          `[useEntityRealtime] Join ${entityType}:${entityId} not queued`,
          {
            instanceId: instanceId.current,
            socketId: currentSocketId,
            isReady,
            hasSocket: Boolean(socket),
            activeConnectionCount: connectionRegistry.get(entityId) || 0,
          },
        )
      }

      // Use the memoized socket names
      debugLog(
        `[useEntityRealtime] Socket names for ${entityType}:`,
        socketNames,
      )

      // Create handlers
      const handleCreated = createSocketHandler(
        'created',
        'onEntityCreated',
        socketNames.created,
      )
      const handleUpdated = createSocketHandler(
        'updated',
        'onEntityUpdated',
        socketNames.updated,
      )
      const handleDeleted = createSocketHandler(
        'deleted',
        'onEntityDeleted',
        socketNames.deleted,
      )

      // Set up socket event listeners
      debugLog(
        `[useEntityRealtime] Setting up listeners for ${entityType}:${entityId}`,
        socketNames,
      )
      ;(socket as ExtendedSocket).on(socketNames.created, handleCreated)
      ;(socket as ExtendedSocket).on(socketNames.updated, handleUpdated)
      ;(socket as ExtendedSocket).on(socketNames.deleted, handleDeleted)

      // Also register for broadcast events from other tabs
      const cleanupCreated = addEventListener(
        socketToBroadcastTypeMap[socketNames.created] || socketNames.created,
        (data: unknown) => {
          const typedData = data as Record<string, unknown>
          if (
            (typedData.id === entityId ||
              typedData[`${entityType}Id`] === entityId) &&
            optionsRef.current.onEntityCreated
          ) {
            optionsRef.current.onEntityCreated(typedData)
          }
        },
      )

      const cleanupUpdated = addEventListener(
        socketToBroadcastTypeMap[socketNames.updated] || socketNames.updated,
        (data: unknown) => {
          const typedData = data as Record<string, unknown>
          if (
            (typedData.id === entityId ||
              typedData[`${entityType}Id`] === entityId) &&
            optionsRef.current.onEntityUpdated
          ) {
            optionsRef.current.onEntityUpdated(typedData)
          }
        },
      )

      const cleanupDeleted = addEventListener(
        socketToBroadcastTypeMap[socketNames.deleted] || socketNames.deleted,
        (data: unknown) => {
          const typedData = data as Record<string, unknown>
          if (
            (typedData.id === entityId ||
              typedData[`${entityType}Id`] === entityId) &&
            optionsRef.current.onEntityDeleted
          ) {
            optionsRef.current.onEntityDeleted(typedData)
          }
        },
      )

      if (DEBUG_MODE && !optionsRef.current.suppressLogging) {
        debugLog(`[${entityType}:${entityId.substring(0, 8)}] Joined room`)
      }

      // Cleanup function
      return () => {
        if (socket) {
          // Only if this is the last component using this entity, remove listeners and leave room
          const remainingConnections = connectionRegistry.get(entityId) || 0

          if (remainingConnections <= 1) {
            // Remove event listeners
            const socketClient = socket as ExtendedSocket
            socketClient.off(socketNames.created, handleCreated)
            socketClient.off(socketNames.updated, handleUpdated)
            socketClient.off(socketNames.deleted, handleDeleted)

            // Clean up broadcast event listeners
            cleanupCreated()
            cleanupUpdated()
            cleanupDeleted()

            // Leave entity room
            leaveRoom(entityType, entityId)
            if (!optionsRef.current.suppressLogging) {
              debugLog(
                `[useEntityRealtime:${instanceId.current}] Left ${entityType} ${entityId}`,
              )
            }
          }
        }
      }
    }

    return () => {} // Empty cleanup for non-first connections
  }, [
    socket,
    socketId, // Added to detect reconnections and rejoin rooms
    entityId,
    entityType,
    currentOrgId,
    isReady,
    joinRoom,
    leaveRoom,
    addEventListener,
    socketNames,
    connectionRegistry, // Added for cleanup logic
  ])

  // Enhanced client-generated entity tracking with localStorage persistence
  const trackClientEntity = useCallback(
    (id: string) => {
      if (!id) return

      // Add to our tracking set
      clientGeneratedEntitiesRef.current.add(id)

      debugLog(
        `[useEntityRealtime:${instanceId.current}] Tracking client-generated ${entityType}: ${id} (in-memory)`,
      )

      // Auto-cleanup after a while to prevent memory leaks from the Set
      setTimeout(() => {
        clientGeneratedEntitiesRef.current.delete(id)

        debugLog(
          `[useEntityRealtime:${instanceId.current}] Removed tracking for ${entityType}: ${id} (in-memory)`,
        )
      }, 30000) // 30 seconds
    },
    [entityType],
  )

  // Add a custom event listener
  const addSocketEventListener = useCallback(
    <T = Record<string, unknown>>(
      eventName: string,
      handler: (data: T) => void,
      options: EntityEventListenerOptions = {},
    ) => {
      if (!socket || !isReady) return () => {}

      // Create socket event handler that also processes and broadcasts the event
      const socketHandler = (data: Record<string, unknown>) => {
        // First handle locally
        handler(data as T)

        if (options.skipBroadcast) return

        // If relevant to current entity, process and broadcast to other tabs
        // But only if the event didn't already come from a broadcast
        const resolvedEntityId = options.resolveEntityId?.(data)
        const matchesEntity =
          entityId &&
          (resolvedEntityId
            ? resolvedEntityId === entityId
            : data.id === entityId || data[`${entityType}Id`] === entityId)

        if (matchesEntity && data._source !== 'broadcast') {
          const broadcastEventType =
            socketToBroadcastTypeMap[eventName] || eventName

          // Add source tracking to prevent infinite broadcast loops
          const dataWithSource = {
            ...data,
            _source: 'broadcast',
          }

          debugLog(
            `[useEntityRealtime:${instanceId.current}] Broadcasting message: ${broadcastEventType}`,
          )
          processSocketEvent(
            broadcastEventType,
            entityType,
            entityId,
            dataWithSource,
          )
        } else if (data._source === 'broadcast') {
          debugLog(
            `[useEntityRealtime:${instanceId.current}] Skipping re-broadcast of message: ${eventName}`,
          )
        }
      }

      // Listen for socket events
      const socketClient = socket as ExtendedSocket
      socketClient.on(eventName, socketHandler)

      // Also listen for broadcast events from other tabs
      const broadcastType = socketToBroadcastTypeMap[eventName] || eventName
      const cleanupBroadcast = addEventListener(broadcastType, handler)

      // Return cleanup function
      return () => {
        socketClient.off(eventName, socketHandler)
        cleanupBroadcast()
      }
    },
    [
      socket,
      isReady,
      entityId,
      entityType,
      processSocketEvent,
      addEventListener,
    ],
  )

  /**
   * Check if an entity was client-generated
   */
  const isClientGenerated = useCallback((id: string) => {
    return clientGeneratedEntitiesRef.current.has(id)
  }, [])

  return {
    isConnected: isReady,
    socketId, // Exposed for detecting socket reconnections
    activeConnections: connectionRegistry.get(entityId || '') || 0,
    trackClientEntity,
    isClientGenerated,
    addEventListener: addSocketEventListener,
    instanceId: instanceId.current,

    // Raw access if needed for custom logic
    socket,
    currentOrgId,
    markEventProcessed,
    hasProcessedEvent,
    broadcastToTabs,

    // For debugging
    debug: {
      clientGeneratedCount: clientGeneratedEntitiesRef.current.size,
    },
  }
}
